
import React, { useState } from 'react';
import { m, AnimatePresence } from 'motion/react';
import { ChevronLeft, ChevronRight, AlertTriangle, ShieldCheck, Globe, Euro, MessageCircle, Zap } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const SLIDES = [
  {
    id: 'bureaucracy',
    problem: {
      tag: "THE SCARES",
      title: "Austrian Bureaucracy & Tax Anxiety",
      desc: "Missing a critical SVS deadline, getting an unexpected € 2.000 penalty, or overpaying taxes by thousands because you missed a deduction rule you didn't know existed."
    },
    solution: {
      tag: "PSTAX GUARDIANSHIP",
      title: "Total Peace of Mind",
      desc: "We track every deadline, handle all Finanzamt communication, and proactively optimize your tax and social security. We act as your shield against bureaucracy so you never have to worry about confusing letters again.",
      icon: <ShieldCheck className="text-pstax-blue" size={32} />
    }
  },
  {
    id: 'language',
    problem: {
      tag: "COMMUNICATION",
      title: "The 'Amtsdeutsch' Barrier",
      desc: "Being new in the country and receiving 5-page letters in complex German from the tax office. Even with Google Translate, you're never quite sure what they actually want from you."
    },
    solution: {
      tag: "ENGLISH-FIRST SUPPORT",
      title: "Talk to us in English",
      desc: "Over 50% of our clients are non-German natives. We explain everything in clear English. Need help with a weird letter? Just forward it to us – we'll translate the action items for you.",
      icon: <Globe className="text-pstax-blue" size={32} />
    }
  },
  {
    id: 'pricing',
    problem: {
      tag: "COSTS",
      title: "Surprise Invoices & Hourly Traps",
      desc: "Traditional tax advisors often bill by the minute. You call for a 5-minute question and receive a surprise € 150 invoice two weeks later. It makes you afraid to talk to your own advisor."
    },
    solution: {
      tag: "FIXED PRICING",
      title: "Zero Hidden Fees",
      desc: "We work with fixed annual packages. You know your costs before you even start. No billing per email, no surprise 'consultation' fees. Budgeting for your business has never been easier.",
      icon: <Euro className="text-pstax-blue" size={32} />
    }
  },
  {
    id: 'questions',
    problem: {
      tag: "SUPPORT",
      title: "The 'Guessing' Risk",
      desc: "Because you're worried about the clock ticking on billable hours, you guess how to categorize an expense or how to draft an invoice – and end up with a mess at the end of the year."
    },
    solution: {
      tag: "OPEN DOOR POLICY",
      title: "Ask anything, anytime",
      desc: "We encourage questions. We'd rather spend 5 minutes answering you now than 5 hours fixing a mistake later. It's all covered in your package. No hesitation, no barriers.",
      icon: <MessageCircle className="text-pstax-blue" size={32} />
    }
  },
  {
    id: 'specialization',
    problem: {
      tag: "FOCUS",
      title: "The Generalist Gap",
      desc: "Most tax firms focus on GmbHs and large corporations. As a solo freelancer, you're often their 'smallest' client and don't get the specialized expertise your unique situation requires."
    },
    solution: {
      tag: "FREELANCER SPECIALISTS",
      title: "We speak 'Freelance'",
      desc: "We focus 100% on self-employed professionals and one-person businesses. We know every specific deduction, every SVS quirk, and every optimization strategy relevant to your world.",
      icon: <Zap className="text-pstax-blue" size={32} />
    }
  }
];

export const WhyUsCarousel: React.FC = () => {
  const { t } = useLanguage();
  const carouselT = t.carousel;
  const slides = carouselT.slides;
  const [current, setCurrent] = useState(0);

  const next = () => setCurrent((prev) => (prev + 1) % slides.length);
  const prev = () => setCurrent((prev) => (prev - 1 + slides.length) % slides.length);

  const slideIcons = [
    <ShieldCheck className="text-pstax-blue" size={32} />,
    <Globe className="text-pstax-blue" size={32} />,
    <Euro className="text-pstax-blue" size={32} />,
    <MessageCircle className="text-pstax-blue" size={32} />,
    <Zap className="text-pstax-blue" size={32} />
  ];

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slides.length]);

  return (
    <section id="why-us" className="py-24 bg-pstax-bg scroll-mt-20 overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="text-center mb-6">
          <h2 className="text-4xl font-display font-bold mb-4">{carouselT.title}</h2>
          <p className="text-pstax-gray max-w-3xl mx-auto text-lg leading-relaxed">
            {carouselT.sub}
          </p>
        </div>

        <div className="relative max-w-7xl mx-auto overflow-hidden lg:overflow-visible">
          <div className="flex items-center justify-center">
            {/* Carousel Container */}
            <div className="relative w-full flex items-center justify-center py-4">
              {/* Previous Slide Preview */}
              <div className="absolute hidden lg:block left-0 w-[600px] h-[400px] bg-white rounded-[2.5rem] border border-pstax-border pointer-events-none opacity-20 -translate-x-[85%] scale-90 overflow-hidden">
                <div className="flex h-full">
                  <div className="p-10 bg-red-50/50 w-1/2 flex flex-col justify-center">
                    <h3 className="text-xl font-display font-bold text-pstax-navy mb-2">{slides[(current - 1 + slides.length) % slides.length].problem.title}</h3>
                    <p className="text-pstax-gray text-sm line-clamp-3">{slides[(current - 1 + slides.length) % slides.length].problem.desc}</p>
                  </div>
                  <div className="p-10 bg-emerald-50/50 w-1/2 flex flex-col justify-center">
                    <h3 className="text-xl font-display font-bold text-pstax-navy mb-2">{slides[(current - 1 + slides.length) % slides.length].solution.title}</h3>
                    <p className="text-pstax-gray text-sm line-clamp-3">{slides[(current - 1 + slides.length) % slides.length].solution.desc}</p>
                  </div>
                </div>
              </div>

              <AnimatePresence mode="wait">
                <m.div
                  key={current}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className="relative z-10 w-full max-w-[900px] min-h-[450px] bg-white rounded-[2.5rem] border border-pstax-border shadow-2xl shadow-pstax-blue/10 overflow-hidden flex flex-col md:flex-row"
                >
                  {/* Problem Left */}
                  <div className="p-10 bg-red-50/50 border-b md:border-b-0 md:border-r border-pstax-border md:w-1/2 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-red-600">
                        <AlertTriangle size={20} />
                      </div>
                      <span className="text-[14px] font-black tracking-[0.2em] uppercase text-red-500/80">{slides[current].problem.tag}</span>
                    </div>
                    <h3 className="text-2xl font-display font-bold text-pstax-navy mb-4">{slides[current].problem.title}</h3>
                    <p className="text-pstax-gray text-base leading-relaxed">{slides[current].problem.desc}</p>
                  </div>

                  {/* Solution Right - Light Green Background */}
                  <div className="p-10 flex-1 flex flex-col justify-center bg-emerald-50/50 text-pstax-navy md:w-1/2">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                        {slideIcons[current]}
                      </div>
                      <span className="text-[14px] font-black tracking-[0.2em] uppercase text-emerald-600/80">{slides[current].solution.tag}</span>
                    </div>
                    <h3 className="text-2xl font-display font-bold mb-4 text-pstax-navy">{slides[current].solution.title}</h3>
                    <p className="text-pstax-gray text-base leading-relaxed">{slides[current].solution.desc}</p>
                  </div>
                </m.div>
              </AnimatePresence>

              {/* Next Slide Preview */}
              <div className="absolute hidden lg:block right-0 w-[600px] h-[400px] bg-white rounded-[2.5rem] border border-pstax-border pointer-events-none opacity-40 translate-x-[85%] scale-90 overflow-hidden">
                <div className="flex h-full">
                  <div className="p-10 bg-red-50/50 w-1/2 flex flex-col justify-center">
                    <h3 className="text-xl font-display font-bold text-pstax-navy mb-2">{slides[(current + 1) % slides.length].problem.title}</h3>
                    <p className="text-pstax-gray text-sm line-clamp-3">{slides[(current + 1) % slides.length].problem.desc}</p>
                  </div>
                  <div className="p-10 bg-emerald-50/50 w-1/2 flex flex-col justify-center">
                    <h3 className="text-xl font-display font-bold text-pstax-navy mb-2">{slides[(current + 1) % slides.length].solution.title}</h3>
                    <p className="text-pstax-gray text-sm line-clamp-3">{slides[(current + 1) % slides.length].solution.desc}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button 
              onClick={prev}
              className="absolute left-0 lg:-left-4 z-20 w-12 h-12 rounded-full bg-white border border-pstax-border flex items-center justify-center text-pstax-navy hover:bg-pstax-blue hover:text-white hover:border-pstax-blue transition-all shadow-xl"
              aria-label="Previous slide"
            >
              <ChevronLeft size={24} />
            </button>
            <button 
              onClick={next}
              className="absolute right-0 lg:-right-4 z-20 w-12 h-12 rounded-full bg-white border border-pstax-border flex items-center justify-center text-pstax-navy hover:bg-pstax-blue hover:text-white hover:border-pstax-blue transition-all shadow-xl"
              aria-label="Next slide"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          {/* Dots */}
          <div className="flex justify-center mt-8 gap-2">
            {slides.map((_: any, i: number) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`h-2 transition-all rounded-full ${current === i ? 'w-8 bg-pstax-blue' : 'w-2 bg-pstax-border hover:bg-pstax-blue/30'}`}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={current === i ? 'true' : 'false'}
              />
            ))}
          </div>

          {/* Mobile Arrows */}
          <div className="flex md:hidden justify-center mt-6 gap-4">
            <button 
              onClick={prev}
              className="w-10 h-10 rounded-full bg-white border border-pstax-border flex items-center justify-center text-pstax-navy shadow-sm"
              aria-label="Previous slide"
            >
              <ChevronLeft size={20} />
            </button>
            <button 
              onClick={next}
              className="w-10 h-10 rounded-full bg-white border border-pstax-border flex items-center justify-center text-pstax-navy shadow-sm"
              aria-label="Next slide"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
